import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { createNotificationsHub, type NotificationsHub } from '@/api/notificationsHub'
import { useWaiverStore } from '@/stores/waiver.store'
import { useTradeStore } from '@/stores/trade.store'
import type { Notification, Trade, WaiverStatus } from '@/types/domain'

const MAX_ITEMS = 50

/**
 * Live notifications for the signed-in user, driven entirely by the `/hubs/notifications` SignalR
 * connection (there is no per-user REST history endpoint in M4). `connect()` is idempotent and is
 * called once when the user is authenticated (App shell); `disconnect()` is called on sign-out.
 *
 * The hub is the single notifications connection, so this store also fans `WaiverProcessed` /
 * `TradeProposed` / `TradeResolved` pushes into the waiver/trade stores so their lists update live
 * without each view holding its own connection.
 */
export const useNotificationStore = defineStore('notification', () => {
  const items = ref<Notification[]>([])
  const unread = ref(0)
  const connected = ref(false)

  let hub: NotificationsHub | null = null

  const hasUnread = computed(() => unread.value > 0)

  /** Adds a notification to the top of the list (capped) and bumps the unread count. */
  function addNotification(notification: Notification): void {
    items.value = [notification, ...items.value].slice(0, MAX_ITEMS)
    unread.value += 1
  }

  /** Marks all notifications read (e.g. when the dropdown is opened). */
  function markAllRead(): void {
    unread.value = 0
  }

  /** Wires SignalR (idempotent) and joins the caller's user group. Safe to call repeatedly. */
  async function connect(): Promise<void> {
    if (hub) return
    const waiverStore = useWaiverStore()
    const tradeStore = useTradeStore()
    hub = createNotificationsHub()
    await hub.connect({
      onStatusChange: (isConnected) => {
        connected.value = isConnected
      },
      onNotification: (notification) => {
        addNotification(notification)
      },
      onWaiverProcessed: (event) => {
        waiverStore.applyWaiverProcessed(event.claimId, event.status as WaiverStatus)
      },
      onTradeProposed: (trade: Trade) => {
        tradeStore.upsert(trade)
      },
      onTradeResolved: (trade: Trade) => {
        tradeStore.upsert(trade)
      },
    })
  }

  async function disconnect(): Promise<void> {
    if (hub) {
      await hub.disconnect()
      hub = null
    }
    connected.value = false
  }

  /** Clears state (e.g. on sign-out, after disconnecting). */
  function reset(): void {
    items.value = []
    unread.value = 0
    connected.value = false
  }

  return {
    // state
    items,
    unread,
    connected,
    // derived
    hasUnread,
    // actions
    addNotification,
    markAllRead,
    connect,
    disconnect,
    reset,
  }
})
